import React from "react";
import {
    FormSection, SectionHeader, SectionBody,
    AlgoContainer, AlgoStep, AlgoBox, AlgoArrow,
} from "./EMTCTStyles";

/**
 * EMTCTTestingAlgorithm — Visual testing algorithm:
 *   Determine → STAT-PAK → SD Bioline (if discordant)
 */
export const EMTCTTestingAlgorithm = function(props) {
    const testingStep = props.testingStep;
    const formData = props.formData;

    const steps = [
        {
            key: "determine",
            name: "Determine",
            desc: "1st rapid test",
            field: "woman_test_result",
        },
        {
            key: "statpak",
            name: "STAT-PAK",
            desc: "Confirmatory test",
            field: "woman_confirmatory_result",
        },
        {
            key: "bioline",
            name: "SD Bioline",
            desc: "Tie-breaker (if discordant)",
            field: "woman_retest_result",
        },
    ];

    const getStepState = function(step, idx) {
        if (idx === 0) {
            if (formData.woman_test_result) return { active: false, done: true };
            return { active: true, done: false };
        }
        if (idx === 1) {
            if (!formData.woman_test_result || formData.woman_test_result === "non_reactive") {
                return { active: false, done: false };
            }
            if (formData.woman_confirmatory_result) return { active: false, done: true };
            return { active: true, done: false };
        }
        if (idx === 2) {
            if (!formData.woman_confirmatory_result) return { active: false, done: false };
            if (formData.woman_test_result === "reactive" &&
                formData.woman_confirmatory_result === "non_reactive") {
                if (formData.woman_retest_result) return { active: false, done: true };
                return { active: true, done: false };
            }
            return { active: false, done: false };
        }
        return { active: false, done: false };
    };

    const getResultDisplay = function(val) {
        if (!val) return null;
        if (val === "reactive") {
            return React.createElement("span", {
                style: { background: "#ffcdd2", color: "#b71c1c" },
                className: "algo-result",
            }, "⊕ Reactive");
        }
        return React.createElement("span", {
            style: { background: "#c8e6c9", color: "#2e7d32" },
            className: "algo-result",
        }, "⊖ Non-Reactive");
    };

    /* ─── Final status ─── */
    const renderFinalStatus = function() {
        if (testingStep === "negative") {
            return React.createElement("div", {
                style: {
                    textAlign: "center", padding: "12px 20px", background: "#e8f5e9",
                    borderRadius: 8, border: "1.5px solid #a5d6a7", marginTop: 16,
                }
            },
                React.createElement("span", { style: { fontSize: "1.2rem" } }, "✅"),
                React.createElement("span", { style: { fontSize: "0.88rem", fontWeight: 700, color: "#2e7d32", marginLeft: 8 } },
                    "HIV NEGATIVE — Retest in 3rd trimester and at labour")
            );
        }
        if (testingStep === "confirmed_positive") {
            return React.createElement("div", {
                style: {
                    textAlign: "center", padding: "12px 20px", background: "#ffebee",
                    borderRadius: 8, border: "1.5px solid #ef9a9a", marginTop: 16,
                }
            },
                React.createElement("span", { style: { fontSize: "1.2rem" } }, "🔴"),
                React.createElement("span", { style: { fontSize: "0.88rem", fontWeight: 700, color: "#b71c1c", marginLeft: 8 } },
                    "HIV POSITIVE CONFIRMED — Initiate/Continue ART")
            );
        }
        if (testingStep === "discordant") {
            return React.createElement("div", {
                style: {
                    textAlign: "center", padding: "12px 20px", background: "#fff3e0",
                    borderRadius: 8, border: "1.5px solid #ffcc80", marginTop: 16,
                }
            },
                React.createElement("span", { style: { fontSize: "1.2rem" } }, "⚠"),
                React.createElement("span", { style: { fontSize: "0.88rem", fontWeight: 700, color: "#e65100", marginLeft: 8 } },
                    "DISCORDANT — Proceed to SD Bioline (tie-breaker)")
            );
        }
        if (testingStep === "needs_confirmatory") {
            return React.createElement("div", {
                style: {
                    textAlign: "center", padding: "12px 20px", background: "#fff3e0",
                    borderRadius: 8, border: "1.5px solid #ffcc80", marginTop: 16,
                }
            },
                React.createElement("span", { style: { fontSize: "1.2rem" } }, "➡"),
                React.createElement("span", { style: { fontSize: "0.88rem", fontWeight: 700, color: "#e65100", marginLeft: 8 } },
                    "Determine REACTIVE — Proceed to STAT-PAK confirmatory test")
            );
        }
        return null;
    };

    return React.createElement(FormSection, null,
        React.createElement(SectionHeader, null,
            React.createElement("h4", null, "🧬 HIV Testing Algorithm (Uganda National)"),
            React.createElement("span", { className: "sh-badge" }, "Serial Testing")
        ),
        React.createElement(SectionBody, null,
            React.createElement("p", {
                style: { fontSize: "0.8rem", color: "#78909c", margin: "0 0 12px", textAlign: "center" }
            },
                "Uganda follows a serial rapid testing algorithm: Determine → STAT-PAK → SD Bioline"
            ),
            React.createElement(AlgoContainer, null,
                steps.map(function(step, idx) {
                    const state = getStepState(step, idx);
                    const result = formData[step.field];
                    return React.createElement(React.Fragment, { key: step.key },
                        idx > 0 ? React.createElement(AlgoArrow, null, "→") : null,
                        React.createElement(AlgoStep, { active: state.active, done: state.done },
                            React.createElement(AlgoBox, { className: "algo-box" },
                                React.createElement("div", { className: "algo-name" }, step.name),
                                React.createElement("div", { className: "algo-desc" }, step.desc),
                                result ? getResultDisplay(result) : null
                            )
                        )
                    );
                })
            ),
            renderFinalStatus()
        )
    );
};
